import { useRef, useState } from 'react';
import { MAX_UPLOAD_KB, type UploadedFile } from '@/shared/api/upload';
import { FormField } from './FormField';

type Props = {
  label: string;
  files: UploadedFile[];
  onChange: (files: UploadedFile[]) => void;
  upload: (file: File) => Promise<UploadedFile>;
  remove: (id: number) => Promise<unknown>;
  /** Allow several files; a new upload replaces the current one otherwise. */
  multiple?: boolean;
  /** Show the file URL under each image (for pasting into HTML bodies). */
  showUrl?: boolean;
  errors?: string[];
  accept?: string;
  previewWidth?: number;
};

const DEFAULT_ACCEPT = '.jpg, .jpeg, .png, .gif';
const DEFAULT_PREVIEW_WIDTH = 200;

const tooBig = (file: File) => `Файл ${file.name} слишком большой. Максимальный размер — ${MAX_UPLOAD_KB / 1000} МБ.`;

/** File input uploading immediately (legacy behaviour) with previews and per-file delete. */
export const FileUploadField = ({
  label,
  files,
  onChange,
  upload,
  remove,
  multiple,
  showUrl,
  errors,
  accept = DEFAULT_ACCEPT,
  previewWidth = DEFAULT_PREVIEW_WIDTH,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSelect = async (selected: FileList | null) => {
    const picked = Array.from(selected ?? []);
    if (!picked.length) return;
    const oversized = picked.find((file) => file.size / 1024 > MAX_UPLOAD_KB);
    if (oversized) {
      setError(tooBig(oversized));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const uploaded: UploadedFile[] = [];
      for (const file of picked) uploaded.push(await upload(file));
      onChange(multiple ? [...files, ...uploaded] : uploaded.slice(-1));
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Не удалось загрузить файл');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async (id: number) => {
    setBusy(true);
    setError('');
    try {
      await remove(id);
      onChange(files.filter((file) => file.id !== id));
    } catch {
      setError('Не удалось удалить файл');
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormField label={label} errors={error ? [error] : errors}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={busy}
        onChange={(e) => void handleSelect(e.target.files)}
      />
      {busy && <div>Загрузка…</div>}
      {files.map((file) => (
        <div key={file.id} style={{ marginTop: 8 }}>
          <img src={file.thumbUrl ?? file.url} alt="" style={{ width: previewWidth, objectFit: 'cover' }} />
          {showUrl && (
            <div>
              <strong>Ссылка:</strong> {file.url}
            </div>
          )}
          <button
            type="button"
            className="btn btn-danger btn-sm"
            style={{ display: 'block', marginTop: 4 }}
            disabled={busy}
            onClick={() => void handleRemove(file.id)}
          >
            Удалить
          </button>
        </div>
      ))}
    </FormField>
  );
};
